/**
 * Zustand Store for Shift Assignments
 */

import { create } from 'zustand';
import { api } from '../lib/api';
import type { EmployeeShiftAssignment, CreateAssignmentFormData, UpdateAssignmentFormData } from '../types/shifts';

interface ShiftAssignmentsState {
  assignments: EmployeeShiftAssignment[];
  loading: boolean;
  error: string | null;
  fetchAssignments: (filters?: { user_id?: string; shift_schedule_id?: string; is_active?: boolean }) => Promise<void>;
  createAssignment: (data: CreateAssignmentFormData) => Promise<EmployeeShiftAssignment>;
  updateAssignment: (id: string, data: UpdateAssignmentFormData) => Promise<EmployeeShiftAssignment>;
  deleteAssignment: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useShiftAssignmentsStore = create<ShiftAssignmentsState>((set) => ({
  assignments: [],
  loading: false,
  error: null,

  fetchAssignments: async (filters) => {
    set({ loading: true, error: null });
    try {
      const assignments = await api.getShiftAssignments(filters);
      set({ assignments, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  createAssignment: async (data) => {
    set({ loading: true, error: null });
    try {
      const newAssignment = await api.createShiftAssignment(data);
      
      set(state => ({
        assignments: [...state.assignments, newAssignment],
        loading: false,
      }));
      
      return newAssignment;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateAssignment: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const updatedAssignment = await api.updateShiftAssignment(id, data);
      
      set(state => ({
        assignments: state.assignments.map(a => a.id === id ? updatedAssignment : a),
        loading: false,
      }));
      
      return updatedAssignment;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteAssignment: async (id) => {
    set({ loading: true, error: null });
    try {
      await api.deleteShiftAssignment(id);
      
      set(state => ({
        assignments: state.assignments.filter(a => a.id !== id),
        loading: false,
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
